import { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '@/utils/date';
import styles from './CharmingNaturalHeader.module.css';

type CharmingNaturalHeaderProps = {
  brideName: string;
  groomName: string;
};

/**
 * Header для премиум layout "Charming Natural"
 * Фиксированный header с логотипом, датой и навигацией
 */
const CharmingNaturalHeader = memo(({ brideName, groomName }: CharmingNaturalHeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Получаем первые буквы имен для логотипа
  const logo = `${brideName.charAt(0).toUpperCase()}&${groomName.charAt(0).toUpperCase()}`;

  // Текущая дата
  const currentDate = formatDate(new Date());

  // Функция для плавной прокрутки к секции
  const scrollToSection = useCallback((sectionId: string) => {
    // Закрываем мобильное меню при клике на ссылку
    setIsMobileMenuOpen(false);

    if (sectionId === 'main') {
      // Для секции main прокручиваем к началу страницы
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={styles.header}
    >
      <div className={styles.container}>
        {/* Левая часть: логотип и дата */}
        <div className={styles.leftSection}>
          <button
            type="button"
            onClick={() => scrollToSection('main')}
            className={styles.logo}
            aria-label="Перейти на главную"
          >
            {logo}
          </button>
          <div className={styles.date}>{currentDate}</div>
        </div>

        {/* Кнопка меню для мобильных */}
        <button
          type="button"
          onClick={toggleMobileMenu}
          className={styles.menuButton}
          aria-label="Открыть меню"
          aria-expanded={isMobileMenuOpen}
        >
          <span className={`${styles.menuIcon} ${isMobileMenuOpen ? styles.menuIconOpen : ''}`}>
            <span />
            <span />
            <span />
          </span>
        </button>

        {/* Навигация для десктопа */}
        <nav className={styles.navigation}>
          <button
            type="button"
            onClick={() => scrollToSection('main')}
            className={styles.navLink}
          >
            Главная
          </button>
          <button
            type="button"
            onClick={() => scrollToSection('rsvp')}
            className={styles.navLink}
          >
            Подтвердить присутствие
          </button>
        </nav>

        {/* Мобильная навигация */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className={styles.mobileNavigation}
            >
              <button
                type="button"
                onClick={() => scrollToSection('main')}
                className={styles.mobileNavLink}
              >
                Главная
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('rsvp')}
                className={styles.mobileNavLink}
              >
                Подтвердить присутствие
              </button>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
});

CharmingNaturalHeader.displayName = 'CharmingNaturalHeader';

export default CharmingNaturalHeader;
